import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { RadioCardComponent } from '../controllers/radio-card/radio-card.component';
import { FormsModule } from '@angular/forms';
import { AddressSelectComponent } from '../address-select/address-select.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BaseWooCommerceAddressDetails } from '../../../core/interfaces/woo-commerce/woo-commerce.interface';
import {
  InputAddressComponent,
  InputAddressSize
} from '../controllers/input-address/input-address.component';
import { select } from '@ngxs/store';
import {
  RestaurantGeneralDeliveryTypes,
  RestaurantGeneralLeadTime,
  RestaurantState
} from '../../state/restaurant/restaurant.state';
import { ShippingMethod } from '../../../core/enums/woo-commerce/shipping/shipping.enum';
import {
  WooCommerceDeliveryAddress,
  WooCommerceShippingAddress
} from '../../../core/interfaces/woo-commerce/api/woo-commerce-api.interface';
import { getShippingOptions } from '../../constants/shipping-options.constant';
import { OptionSwitcherItem } from '../../interfaces/option-switcher.interface';
import { HappyStoreSwitchService } from '../../../core/services/happy/happy-store-switch.service';

export interface DeliveryMethodSelectedAddress {
  method: ShippingMethod;
  address: WooCommerceDeliveryAddress | WooCommerceShippingAddress;
}

@UntilDestroy()
@Component({
  selector: 'app-shipping-method',
  imports: [
    RadioCardComponent,
    FormsModule,
    AddressSelectComponent,
    InputAddressComponent
  ],
  templateUrl: './shipping-method.component.html',
  styleUrl: './shipping-method.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShippingMethodComponent {
  private readonly happyStoreSwitchService = inject(HappyStoreSwitchService);

  protected readonly InputAddressSize = InputAddressSize;
  protected readonly ShippingMethod = ShippingMethod;
  protected readonly SHIPPING_OPTIONS: Signal<OptionSwitcherItem[]> = computed(
    () =>
      getShippingOptions(
        this.leadTimes().delivery_lead_time,
        this.leadTimes().pick_up_lead_time,
        this.deliveryTypes()
      )
  );

  selectAddress: OutputEmitterRef<DeliveryMethodSelectedAddress> =
    output<DeliveryMethodSelectedAddress>();

  shippingMethod: WritableSignal<string> = signal<string>('');

  leadTimes: Signal<RestaurantGeneralLeadTime> = select(
    RestaurantState.leadTimes
  );
  deliveryTypes: Signal<RestaurantGeneralDeliveryTypes> = select(
    RestaurantState.deliveryTypes
  );

  // Pickup
  pickupAddresses: Signal<WooCommerceShippingAddress[]> = select(
    RestaurantState.pickupLocations
  );
  selectedPickupAddress: WritableSignal<BaseWooCommerceAddressDetails | null> =
    signal<BaseWooCommerceAddressDetails | null>(null);
  lastConfirmedPickupAddress: WritableSignal<BaseWooCommerceAddressDetails | null> =
    signal<BaseWooCommerceAddressDetails | null>(null);

  // Delivery
  deliveryAddress: WritableSignal<string> = signal<string>('');
  isDeliveryAddressAvailable: WritableSignal<boolean> = signal<boolean>(true);

  toggleShippingMethod(shippingMethod: ShippingMethod): void {
    this.shippingMethod.set(shippingMethod);

    if (shippingMethod === ShippingMethod.Pickup) {
      const pickupAddresses = this.pickupAddresses();

      if (pickupAddresses.length === 1) {
        this.selectedPickupAddress.set(pickupAddresses[0]);
        this.onSelectedAddressChange(pickupAddresses[0], ShippingMethod.Pickup);
      }
    }
  }

  onSelectedAddressChange(
    address: WooCommerceDeliveryAddress | WooCommerceShippingAddress,
    method: ShippingMethod = ShippingMethod.Pickup
  ): void {
    if (method !== ShippingMethod.Pickup) {
      this.selectAddress.emit({ method, address });
      return;
    }

    const previousPickupAddress = this.lastConfirmedPickupAddress();
    this.happyStoreSwitchService
      .confirmPickupLocationStore(address as WooCommerceShippingAddress)
      .pipe(
        untilDestroyed(this)
      )
      .subscribe((result) => {
        if (result === 'cancelled') {
          this.selectedPickupAddress.set(previousPickupAddress);
          return;
        }

        if (result === 'switching') {
          return;
        }

        this.lastConfirmedPickupAddress.set(address);
        this.selectAddress.emit({ method, address });
      });
  }
}
